/**
 * One contract: what it says, who signed it, and the form that edits it.
 *
 * The edit form stays a form rather than becoming a modal - it is the whole
 * point of the screen, and a dialog you cannot leave open next to the signature
 * block would make the page a list of one thing. What changed is the surfaces:
 * the CRM's cards, `crm-lab` labels over `crm-inp` fields, and the status
 * banners drawn as notes rather than three hand-rolled colour boxes.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2, ChevronLeft, FileDown, FileSignature, Loader2, PenLine, Save, Send, Trash2,
} from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import {
  deleteContract, downloadContractPdf, getContract, sendContract, signContract, updateContract,
  type Contract, type ContractStatus,
} from "../../../lib/projects";
import { RichTextEditor } from "../../../components/RichTextEditor";
import {
  Btn, Card, CardHead, EmptyState, SelectInput, StatusPill, fmtDate, fmtDateTime, useToast,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { contentToText, dateInput, toIso } from "./lib";
import { STATUSES, statusTone } from "./status";

interface Form {
  title: string;
  type: string;
  value: string;
  start: string;
  end: string;
  content: string;
  status: ContractStatus;
}

const EMPTY: Form = { title: "", type: "", value: "", start: "", end: "", content: "", status: "draft" };

/**
 * A line of context under the head: what the contract is doing right now.
 *
 * Deliberately a `crm-card` with a pill rather than the three tinted boxes this
 * screen used to hand-roll (a blue one for "sent", an amber one for "no email",
 * a green one for the signature). Three bespoke colour schemes on one page is
 * how an application stops looking like one application.
 */
function Note({ tone, icon: Icon, children }: {
  tone: "info" | "wt" | "ok";
  icon: typeof Send;
  children: React.ReactNode;
}) {
  return (
    <div className="crm-card mt-3 flex items-start gap-2.5 p-3.5">
      <StatusPill tone={tone} icon>
        <Icon className="size-3.5" />
      </StatusPill>
      <span className="crm-sub min-w-0 flex-1">{children}</span>
    </div>
  );
}

export function ContractDetail() {
  const t = useT();
  const { locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noEmailWarn, setNoEmailWarn] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    getContract(id)
      .then((c) => {
        setContract(c);
        setForm({
          title: c.title,
          type: c.type ?? "",
          value: c.valueCents != null ? String(c.valueCents / 100) : "",
          start: dateInput(c.startDate),
          end: dateInput(c.endDate),
          content: contentToText(c.content),
          status: c.status,
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (!contract) {
    return <EmptyState icon={FileSignature} title={t("contracts.notFound")} />;
  }

  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    if (!contract) return;
    setSaving(true);
    try {
      setContract(
        await updateContract(contract.id, {
          title: form.title.trim(),
          type: form.type.trim() || undefined,
          valueCents: form.value ? Math.round(parseFloat(form.value) * 100) : null,
          startDate: toIso(form.start),
          endDate: toIso(form.end),
          content: form.content ? { html: form.content } : undefined,
          status: form.status,
        }),
      );
      toast(t("common.saved"));
    } catch {
      toast(t("common.error"));
    } finally {
      setSaving(false);
    }
  }

  async function sign() {
    if (!contract) return;
    const c = await signContract(contract.id);
    setContract(c);
    set({ status: c.status });
  }

  async function send() {
    if (!contract) return;
    const c = await sendContract(contract.id);
    setContract(c);
    set({ status: c.status });
    setNoEmailWarn(c.emailStatus === "skipped");
  }

  async function pdf() {
    if (!contract) return;
    setPdfBusy(true);
    try {
      await downloadContractPdf(contract.id, `${contract.number ?? contract.title}.pdf`);
    } catch {
      toast(t("common.error"));
    } finally {
      setPdfBusy(false);
    }
  }

  const unsent = contract.status === "draft" || contract.status === "sent";

  return (
    <PopHost>
      {/* A real Link, not a button that navigates: back is a place, and the
          browser's own middle-click and history belong to it. */}
      <Link to="/contracts" className="crm-mut mb-3 inline-flex items-center gap-1 text-[12.5px] hover:text-foreground">
        <ChevronLeft className="size-3.5" />
        {t("contracts.back")}
      </Link>

      <div className="crm-head">
        <div className="min-w-0">
          <h1 className="crm-h1 flex flex-wrap items-center gap-2.5">
            {contract.title}
            <StatusPill tone={statusTone(contract.status)}>{t(`contracts.status.${contract.status}`)}</StatusPill>
          </h1>
          <p className="crm-sub">
            <span className="crm-tnum">{contract.number ?? t("contracts.noNumber")}</span>
            {contract.client?.name ? ` · ${contract.client.name}` : ""}
            {contract.signedAt ? ` · ${t("contracts.signedOn", { date: fmtDate(contract.signedAt) })}` : ""}
          </p>
        </div>

        <div className="crm-head-act">
          {unsent ? (
            <Btn tone={contract.status === "sent" ? "plain" : "brand"} onClick={() => void send()}>
              <Send className="size-4" />
              {t(contract.status === "sent" ? "contracts.resend" : "contracts.sendToClient")}
            </Btn>
          ) : null}
          {unsent ? (
            <Btn onClick={() => void sign()}>
              <PenLine className="size-4" />
              {t("contracts.sign")}
            </Btn>
          ) : null}
          <Btn onClick={() => void pdf()} disabled={pdfBusy}>
            {pdfBusy ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
            {t("contracts.downloadPdf")}
          </Btn>
          <Btn tone="danger" onClick={() => setConfirmDel(true)}>
            <Trash2 className="size-4" />
            {t("common.delete")}
          </Btn>
        </div>
      </div>

      {contract.status === "sent" ? (
        <Note tone="info" icon={Send}>
          {t("contracts.sentHint")}
        </Note>
      ) : null}

      {noEmailWarn ? (
        <Note tone="wt" icon={Send}>
          {t("contracts.noEmailWarn")}
        </Note>
      ) : null}

      {contract.signature ? (
        <Note tone="ok" icon={CheckCircle2}>
          {t("contracts.signedBy", { name: contract.signature.name })}
          {contract.signature.signedAt ? ` · ${fmtDateTime(contract.signature.signedAt)}` : ""}
          {contract.signature.ip ? ` · IP ${contract.signature.ip}` : ""}
        </Note>
      ) : null}

      <Card className="mt-3.5">
        <CardHead title={t("projects.details")} />

        {/* `crm-card` is a surface and nothing else - no padding of its own - so
            a body that is not a `crm-rows` has to say where its edges are. */}
        <div className="px-6 pb-5 pt-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <span className="crm-lab">{t("contracts.titleField")}</span>
            <label className="crm-inp">
              <input value={form.title} onChange={(e) => set({ title: e.target.value })} autoComplete="off" />
            </label>
          </div>
          <div>
            <span className="crm-lab">{t("contracts.type")}</span>
            <label className="crm-inp">
              <input
                value={form.type}
                onChange={(e) => set({ type: e.target.value })}
                placeholder={t("contracts.typePlaceholder")}
                autoComplete="off"
              />
            </label>
          </div>
          <div>
            <span className="crm-lab">{t("contracts.value")} (EUR)</span>
            <label className="crm-inp">
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.value}
                onChange={(e) => set({ value: e.target.value })}
              />
            </label>
          </div>
          <div>
            <span className="crm-lab">{t("contracts.statusCol")}</span>
            {/* `SelectInput` rather than `SelectField`: this one is a field in
                a form the operator is filling in, not a filter over a list, so it
                wears the same sunken pill as the text boxes beside it. */}
            <SelectInput
              value={form.status}
              onChange={(next) => set({ status: next as ContractStatus })}
              label={t("contracts.statusCol")}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`contracts.status.${s}`)}
                </option>
              ))}
            </SelectInput>
          </div>
          <div>
            <span className="crm-lab">{t("contracts.startDate")}</span>
            <DatePicker
              value={form.start}
              onChange={(v) => set({ start: v })}
              locale={locale}
              placeholder={t("contracts.startDate")}
            />
          </div>
          <div>
            <span className="crm-lab">{t("contracts.endDate")}</span>
            <DatePicker
              value={form.end}
              onChange={(v) => set({ end: v })}
              locale={locale}
              placeholder={t("contracts.endDate")}
            />
          </div>
        </div>

        <div className="mt-3">
          <span className="crm-lab">{t("contracts.content")}</span>
          <RichTextEditor
            value={form.content}
            onChange={(v) => set({ content: v })}
            placeholder={t("contracts.contentPlaceholder")}
          />
          <p className="crm-fnote mt-1">{t("contracts.contentHint")}</p>
        </div>

        <div className="mt-3.5 flex justify-end">
          <Btn tone="dark" onClick={() => void save()} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            {t("common.save")}
          </Btn>
        </div>
        </div>
      </Card>

      {confirmDel ? (
        <ConfirmModal
          title={t("contracts.deleteTitle")}
          body={t("contracts.deleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await deleteContract(contract.id);
            navigate("/contracts");
          }}
          onClose={() => setConfirmDel(false)}
        />
      ) : null}
    </PopHost>
  );
}
